/**
 * Spec 186 — "How GEO Observatory works" in-app explainer.
 *
 * A single, static, scrollable narrative that teaches GEO Observatory's non-obvious
 * concepts (how LLMs read a site, prompts vs. the engine's fan-out, data
 * collection, personas, and GEO) so logged-in users understand and trust what
 * the platform shows them.
 *
 * Frontend-only: no `/api/` calls, no data fetching, no brand-scoped reads
 * beyond an optional friendly name. Content is copy + hand-built SVG/CSS
 * diagrams. A sticky section nav (scroll-spy + progress) tracks reading
 * position; clicking a nav item smooth-scrolls to that section.
 */
import { useRef } from 'react';
import { Link, NavLink } from 'react-router';
import { Icon } from '../components/ui/Icon.js';
import { useOptionalBrand } from '../lib/brand-context.js';
import { SectionNav } from '../components/how-it-works/SectionNav.js';
import { useScrollSpy } from '../components/how-it-works/use-scroll-spy.js';
import { HOW_IT_WORKS_SECTIONS } from '../components/how-it-works/sections.js';
import { LoopDiagram } from '../components/how-it-works/diagrams/LoopDiagram.js';
import { HumanVsBotDiagram } from '../components/how-it-works/diagrams/HumanVsBotDiagram.js';
import { BotVendorsPanel } from '../components/how-it-works/BotVendorsPanel.js';
import { FanOutDiagram } from '../components/how-it-works/diagrams/FanOutDiagram.js';
import { CollectDiagram } from '../components/how-it-works/diagrams/CollectDiagram.js';
import { PersonaLensDiagram } from '../components/how-it-works/diagrams/PersonaLensDiagram.js';
import { GeoVsSeoDiagram } from '../components/how-it-works/diagrams/GeoVsSeoDiagram.js';

const SECTION_IDS = HOW_IT_WORKS_SECTIONS.map((s) => s.id);

interface SectionProps {
  id: string;
  index: number;
  title: string;
  children: React.ReactNode;
}

/** One self-contained section block: numbered heading + copy + diagram. */
function Section({ id, index, title, children }: SectionProps) {
  // Spec 250 (review P2): the first section starts directly under the intro.
  // Its top padding left ~130px of blank space, and the contents column
  // started level with that space rather than the text. The hairline stays:
  // every reading-archetype section opens on one (gate 233.A.3).
  const first = index === 0;
  return (
    <section
      id={id}
      data-testid={`hiw-section-${id}`}
      aria-labelledby={`${id}-heading`}
      className={`hub-section scroll-mt-6 outline-none${first ? ' pt-0' : ''}`}
    >
      <div className="hub-section__head">
        <h2
          id={`${id}-heading`}
          className="flex items-center gap-3 text-xl font-semibold tracking-tight text-[var(--hub-color-ink)] sm:text-2xl"
        >
          {/* Spec 250 (review P1-15): an ink outline disc. A real sequence, so
              the number stays; the Skagen fill goes. */}
          <span className="hub-num" aria-hidden="true">{index + 1}</span>
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

/** Shared paragraph style for body copy. */
function Lede({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[15px] leading-relaxed text-[var(--hub-color-ink-muted)]">{children}</p>
  );
}

/** `icon` is a Boxicon name, not a component (Spec 230) — this array used to
 * carry the lucide component itself and render it as `<item.icon />`, a
 * component-as-value shape a JSX-tag codemod can't safely rewrite. */
const WHERE_THIS_LIVES = [
  { to: '/fanout', label: 'Fan-out', icon: 'bx-sitemap', blurb: 'See the sub-query map' },
  { to: '/collection', label: 'Collection', icon: 'bx-data', blurb: 'Run prompts across engines' },
  { to: '/personas', label: 'Personas', icon: 'bx-group', blurb: 'Define who you optimize for' },
  { to: '/playbooks', label: 'Playbooks', icon: 'bx-book-open', blurb: 'Turn gaps into actions' },
] as const;

export function HowItWorksPage() {
  const rootRef = useRef<HTMLDivElement>(null);
  // Spec 247: optional, so the tour also renders signed out (PublicHowItWorksPage).
  const brandContext = useOptionalBrand();
  const selectedBrand = brandContext?.selectedBrand;
  const { activeId, progress } = useScrollSpy(SECTION_IDS, rootRef);

  // Optional friendly touch — read-only, no fetch. Falls back to a neutral noun.
  const brandName = selectedBrand?.name?.trim();

  return (
    <div ref={rootRef} data-testid="how-it-works-page" className="hub-page--reading">
      {/* Hero */}
      <header className="mb-8 max-w-3xl">
        {/* Spec 250 (review P1-10): no eyebrow — the title starts the page. It
            was "HOW GEO OBSERVATORY WORKS", tracked capitals in a tinted pill. */}
        <h1 className="hub-page__title">
          The quick tour
        </h1>
        <p className="mt-3 text-base leading-relaxed text-[var(--hub-color-ink-muted)]">
          A few minutes here will make every screen in GEO Observatory make sense. We
          monitor how{' '}
          {brandName ? (
            <span className="font-medium text-[var(--hub-color-ink)]">{brandName}</span>
          ) : (
            'your brand'
          )}{' '}
          shows up in AI-generated answers, find where the gaps are, suggest
          fixes, and track whether they helped. Here is what is happening under
          the hood — in plain language.
        </p>
      </header>

      {/* Two-column: reading column + sticky nav (nav hidden < lg) */}
      <div className="flex flex-col gap-10 lg:flex-row lg:gap-12">
        <article className="min-w-0 max-w-3xl flex-1">
          <Section id="what" index={0} title="What GEO Observatory does">
            <div className="space-y-3">
              <Lede>
                GEO Observatory watches how your brand appears when people ask AI tools a
                question — across ChatGPT, Gemini, Claude, Google AI Overviews
                and Mistral. It looks for the places you should be showing up but
                aren’t, suggests optimization playbooks to close those gaps, and
                then tracks whether the changes actually moved the needle.
              </Lede>
              <Lede>
                That’s the whole loop: <strong>monitor</strong> what the engines
                say, <strong>analyze</strong> the gaps, generate{' '}
                <strong>playbooks</strong>, and <strong>track</strong> the impact
                — then go round again.
              </Lede>
            </div>
            <div className="mt-6">
              {/* Spec 256: the nodes link to their stages in the app; signed
                  out (no brand context) the app is behind sign-in, so plain. */}
              <LoopDiagram linked={brandContext !== null} />
            </div>
          </Section>

          <Section id="llm-view" index={1} title="How LLMs see your site">
            <div className="space-y-3">
              <Lede>
                A crawl bot doesn’t see your site the way a visitor does. It
                doesn’t render the design — it reads the raw text and structure:
                your headings, links, structured data, the rules in{' '}
                <code className="hub-code">robots.txt</code>
                , and increasingly{' '}
                <code className="hub-code">llms.txt</code>{' '}
                — a file that tells AI crawlers what matters.
              </Lede>
              <Lede>
                Later, an answer bot retrieves that machine-readable content and
                synthesizes it into a reply. So for being found and cited, clean
                structure matters more than visual polish — the bot is reading
                the markup, not admiring the page.
              </Lede>
            </div>
            <div className="mt-6">
              <HumanVsBotDiagram />
            </div>
            <div className="mt-6">
              <h3 className="mb-1 text-base font-semibold text-[var(--hub-color-ink)]">
                Who’s actually crawling you
              </h3>
              <p className="mb-4 text-[15px] leading-relaxed text-[var(--hub-color-ink-muted)]">
                Not every AI bot wants the same thing. Some crawl to{' '}
                <strong>train</strong> the models; others crawl to{' '}
                <strong>retrieve and cite</strong> you in answers right now.
              </p>
              <BotVendorsPanel />
            </div>
          </Section>

          <Section id="fan-out" index={2} title="Your prompts vs. the engine’s fan-out">
            <div className="space-y-3">
              <Lede>
                You define prompt templates — the questions you want tested. But
                when a real person asks an AI one question, the engine quietly{' '}
                <strong>fans out</strong> behind the scenes into many narrower
                sub-queries before it answers. GEO Observatory maps that fan-out and
                groups the sub-queries into themed <strong>clusters</strong> so
                you can see the whole question-space, not just your one prompt.
              </Lede>
              <Lede>
                One honest caveat: some of that fan-out is{' '}
                <strong>observed</strong> from real answers, and some is{' '}
                <strong>inferred</strong> to estimate the space around them. So
                treat the breadth as a useful model of demand, not an exact
                measurement of it.
              </Lede>
            </div>
            <div className="mt-6">
              <FanOutDiagram />
            </div>
          </Section>

          <Section id="collect" index={3} title="How GEO Observatory collects data">
            <div className="space-y-3">
              <Lede>
                To gather data, GEO Observatory runs your prompts across all five engines
                with live web-search / grounding turned on — so the answers
                reflect what the engines actually retrieve today, not just what
                they memorized in training.
              </Lede>
              <Lede>
                For every response, it captures the <strong>sources</strong> the
                engine cited or pulled from, and records two things about your
                brand: whether it was <strong>mentioned</strong> at all, and
                whether it was <strong>cited</strong> with a link back to your
                site.
              </Lede>
            </div>
            <div className="mt-6">
              <CollectDiagram />
            </div>
          </Section>

          <Section id="personas" index={4} title="Personas & their role">
            <div className="space-y-3">
              <Lede>
                A persona is who you’re optimizing for — their
                priorities, what supports their wellbeing, and the things they
                distrust (their anti-signals). GEO Observatory scores AI answers{' '}
                <strong>through that persona’s lens</strong>, because the very
                same answer can serve one persona well and another poorly.
              </Lede>
              <Lede>
                Personas shape which prompts get tested and how the answers are
                judged. Below, the same AI answer is scored through two
                lenses — and lands on opposite verdicts. The reason it gives is
                the point: it’s not enough to know the score, you need to know{' '}
                <strong>why</strong> a persona did or didn’t buy the answer.
              </Lede>
            </div>
            <div className="mt-6">
              <PersonaLensDiagram />
            </div>
          </Section>

          <Section id="geo" index={5} title="GEO, in one minute">
            <div className="space-y-3">
              <Lede>
                <strong>GEO</strong> — Generative Engine Optimization — is about
                being the answer an AI engine gives, not just one blue
                link in a list. Where SEO competes for a rank among links, GEO is
                about your share of the answer-space: getting mentioned and cited
                directly inside AI responses.
              </Lede>
              <Lede>
                That’s why the loop matters. GEO Observatory monitors the answers, finds
                where you’re missing, hands you playbooks to fix it, and tracks
                whether your share of the answer goes up.
              </Lede>
            </div>
            <div className="mt-6">
              <GeoVsSeoDiagram />
            </div>
          </Section>

          {/* Closing strip — Where this lives in GEO Observatory */}
          <section
            data-testid="hiw-where-this-lives"
            aria-labelledby="where-this-lives-heading"
            className="hub-section"
          >
            <div className="hub-section__head">
              <h2 id="where-this-lives-heading" className="text-xl font-semibold tracking-tight text-[var(--hub-color-ink)]">
                Where this lives in GEO Observatory
              </h2>
            </div>
            <p className="text-[15px] leading-relaxed text-[var(--hub-color-ink-muted)]">
              Each concept above has its own screen. Jump straight in:
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {WHERE_THIS_LIVES.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="group flex items-center gap-3 rounded-[var(--hub-radius-plate)] border border-[var(--hub-color-line)] bg-[var(--hub-color-surface)] px-4 py-3.5 transition-colors hover:border-[var(--hub-color-line-strong)]"
                >
                  {/* Spec 250 (review P1-15): a neutral disc, not a Skagen tile. */}
                  <span className="hub-disc">
                    <Icon name={item.icon} className="text-lg" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[var(--hub-color-ink)]">
                      {item.label}
                    </span>
                    <span className="block text-xs text-[var(--hub-color-ink-subtle)]">
                      {item.blurb}
                    </span>
                  </span>
                  <Icon
                    name="bx-right-arrow-alt"
                    className="shrink-0 text-base text-[var(--hub-color-ink-subtle)] transition-transform group-hover:translate-x-0.5 group-hover:text-[var(--hub-color-ink)]"
                  />
                </NavLink>
              ))}
            </div>
            <p className="mt-6 text-sm text-[var(--hub-color-ink-subtle)]">
              Want the full picture first?{' '}
              <Link to="/overview" className="font-medium text-[var(--hub-color-action-text)] hover:underline">
                Head back to your overview
              </Link>
              .
            </p>
          </section>
        </article>

        {/* Sticky in-page nav (scroll-spy + progress). Hidden < lg. */}
        <aside className="hidden w-60 shrink-0 lg:block">
          <SectionNav activeId={activeId} progress={progress} />
        </aside>
      </div>
    </div>
  );
}
